import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { settingsApi, getErrorMessage } from '../../services/api';
import { linkExpiryOptions, linkExpiryLabel, FALLBACK_EXPIRY_HOURS } from '../../utils/linkExpiry';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

/**
 * Settings -> Users & Security: how long a document link stays valid unless the sender picks
 * something else while sending. "No expiry" makes links valid until the document is completed
 * or cancelled. Saves as soon as a value is chosen.
 */
export default function DefaultLinkExpiry() {
  const [hours, setHours] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    settingsApi.getLinkExpiry()
      .then(({ data }) => { if (alive) setHours(Number.isInteger(data?.default_hours) ? data.default_hours : FALLBACK_EXPIRY_HOURS); })
      .catch(() => { if (alive) setHours(FALLBACK_EXPIRY_HOURS); });
    return () => { alive = false; };
  }, []);

  const change = async (value) => {
    const next = parseInt(value, 10);
    const previous = hours;
    setHours(next);
    setSaving(true);
    try {
      await settingsApi.setLinkExpiry(next);
      toast.success(next === 0 ? 'Document links will no longer expire by default' : `Document links now expire after ${linkExpiryLabel(next)} by default`);
    } catch (error) {
      setHours(previous);
      toast.error(getErrorMessage(error, 'Failed to save the default link expiry'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-4 p-4 bg-muted/50 rounded-lg" data-testid="default-link-expiry">
      <div>
        <p className="font-medium">Default Link Expiry</p>
        <p className="text-sm text-muted-foreground">
          How long document links remain valid. A sender can still pick a different length when sending.
        </p>
      </div>
      <Select value={hours === null ? undefined : String(hours)} onValueChange={change} disabled={hours === null || saving}>
        <SelectTrigger className="w-48 flex-shrink-0" data-testid="default-link-expiry-select">
          <SelectValue placeholder="Loading…" />
        </SelectTrigger>
        <SelectContent>
          {linkExpiryOptions(hours).map((o) => (
            <SelectItem key={o.hours} value={String(o.hours)}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
